import { Brand, LogoType } from "../../../shared/models/brand.model";

/*
    The four fields a Brand needs (MPN-2174, MPN-1712 AC6): a Primary logo, a Square logo,
    a Primary color and a Secondary color. Nothing blocks saving a Brand without them; the
    Branding tab lists what's missing, and a section can't remove one once it's set.

    A color still at its column default counts as unset, so a deliberately black Primary
    or white Secondary reads as missing too (plan R4).
*/

export const REQUIRED_BRAND_FIELDS = ["Primary logo", "Square logo", "Primary color", "Secondary color"] as const;

export type RequiredBrandField = (typeof REQUIRED_BRAND_FIELDS)[number];

/** What the four are judged on. A section gives only its own; a field left out isn't judged. */
export interface RequiredFieldValues {
    /** The logo type of each live logo. */
    logoTypes?: readonly (LogoType | null)[];
    primaryColor?: string | null;
    secondaryColor?: string | null;
}

// The Brand columns' defaults on the server.
const PRIMARY_DEFAULT = "#000000";
const SECONDARY_DEFAULT = "#FFFFFF";

const isSetColor = (hex: string | null, columnDefault: string) =>
    !!hex && hex !== "#" && hex.toUpperCase() !== columnDefault;

export const missingRequiredFields = ({ logoTypes, primaryColor, secondaryColor }: RequiredFieldValues): RequiredBrandField[] => {
    const isSet: Record<RequiredBrandField, boolean | undefined> = {
        "Primary logo": logoTypes && logoTypes.includes("PRIMARY"),
        "Square logo": logoTypes && logoTypes.includes("SQUARE_FORMAT"),
        "Primary color": primaryColor === undefined ? undefined : isSetColor(primaryColor, PRIMARY_DEFAULT),
        "Secondary color": secondaryColor === undefined ? undefined : isSetColor(secondaryColor, SECONDARY_DEFAULT),
    };
    return REQUIRED_BRAND_FIELDS.filter((field) => isSet[field] === false);
};

/** The required fields set in `saved` that saving `form` would leave unset. */
export const removedRequiredFields = (saved: RequiredFieldValues, form: RequiredFieldValues): RequiredBrandField[] => {
    const missingBefore = missingRequiredFields(saved);
    return missingRequiredFields(form).filter((field) => !missingBefore.includes(field));
};

/** The saved Brand's values: its live logos and its Primary and Secondary color columns. */
export const brandRequiredValues = (brand: Brand): RequiredFieldValues => ({
    logoTypes: (brand.assets ?? []).filter((a) => a.kind === "LOGO").map((a) => a.logo_type),
    primaryColor: brand.primary_color_code,
    secondaryColor: brand.secondary_color_code,
});
